import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, input, output, inject } from '@angular/core';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { PopoverModule } from 'primeng/popover';
import { DividerModule } from 'primeng/divider';
import { TimelineModule } from 'primeng/timeline';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { StyleClassModule } from 'primeng/styleclass';

import { ActivatedRoute, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, Subscription, interval, forkJoin, take, takeUntil, Subject, catchError } from 'rxjs';

import { environment } from '@fitness-force/environments';
import type { GetChargeOrderStatus, ChargeOrderItem, GetOrderStatusOfQuotation, FreezeOrderStatus, 
  NotificationError, SubscriptionSignupCharges, SubscriptionSignUpOrderSummaryResponse, 
  SubscriptionSignupService, SubscriptionSignUpStatus, SubscriptionSignupTaxLineItem,
   SubscriptionSignUpWithBillingJsonResponse, TENANTSINTERFACE } from '@fitness-force/models';
import { SubscriptionService, SubscriptionSignupByUuidService ,ToastMessageService} from '@fitness-force/api';

import { ProgressBarModule } from 'primeng/progressbar';
import { DrawerModule } from 'primeng/drawer';
import { ChargeQuatationStatus, FreezeRequestStatus, LinkType, PlanCategory, PlanIntervalType, SignUpStatus } from '@fitness-force/enum';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import {
  COMPANY_INFO,
  TENANTS_INFO,
} from '@fitness-force/state';
import {
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { QrCodeModule } from 'ng-qrcode';

import { SkeletonModule } from 'primeng/skeleton';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { MessageModule } from 'primeng/message';
import { OrderSubscriptionStatusPollingService } from '../services/order-subscription-status-polling.service';
import {
  PAYMENT_TIMELINE_MAX_POLL_COUNT,
  PAYMENT_TIMELINE_POLL_INTERVAL_MS,
} from '../services/payment-polling.constants';
import {
  evaluateOnlinePaymentPollOutcome,
  getOnlinePaymentFailureReason,
  isSignupSuccessfulStatus,
  resolveSignupTimelineStatusAfterPayment,
} from '../services/signup-payment-poll.util';

@Component({
  selector: 'payment-status-timeline',
  templateUrl: './payment-status-timeline.component.html',
  styleUrls: ['./payment-status-timeline.component.scss'],
  providers: [ToastMessageService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    PopoverModule,
    DividerModule,
    TimelineModule,
    ButtonModule,
    StyleClassModule,
    TooltipModule,
    ProgressBarModule,
    DrawerModule,
    CurrencyformatPipe,
    QrCodeModule,
    SkeletonModule,
    RouterModule,
    TranslateModule,
    MessageModule,
    AsyncPipe,
    DatePipe,
    NgClass
]
})
export class PaymentStatusTimelineComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly subscriptionSignUp = inject(SubscriptionSignupByUuidService); 
  private readonly toastMessageService = inject(ToastMessageService);
  readonly currencyPipe = inject(CurrencyformatPipe);
  private readonly translate = inject(TranslateService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly orderSubscriptionStatusPollingService = inject(OrderSubscriptionStatusPollingService);

  readonly uuid = input('');
  readonly ispayment = input(false);
  readonly showPaymentStatusSidebar = input(false, { alias: "visible" });
  readonly hidePaymentSummaryPopup = output<boolean>();
  readonly GoToClientSubscription = output<{tab: string}>();
  readonly GoToClientProfile = output<{
    clientId: number, 
    tab: string, 
    subscriptionId?: number, 
    freezeRequestId?: number,
    openSidebar?: boolean
  }>();
  readonly linkType = input<LinkType>(LinkType.SignUp);
  readonly skipPaymentPolling = input(false);
  readonly chargePaymentUnsuccessful = input(false);

  subsData$ = new BehaviorSubject({} as SubscriptionSignUpOrderSummaryResponse);
  freezeOrderData$ = new BehaviorSubject<FreezeOrderStatus | null>(null);
  chargeOrderData$ = new BehaviorSubject<GetChargeOrderStatus | null>(null);
  showChargePaymentFailureState = false;
  chargePaymentFailureMessage = '';

  getAccessTenantList$!: Observable<any>;
  tenantId = 0;

  bitlyLink= '';

  showContractSidebar = false;
  showSkeleton$ = new BehaviorSubject<boolean>(true);
  //showSkeleton: boolean = true;
  showLoader = true;
  isOffline = false;

  planIntervalType?: PlanIntervalType;
  paymentStatus2!: any[];
  timerSubscription!: Subscription;
  subscriptionSignUpStatusArray: SubscriptionSignUpStatus[] = [];
  subscriptionSignUpStatus$ = new BehaviorSubject<SubscriptionSignUpStatus[]>([]);
  clientId!: number;
  subscriptionId!: number;
  waiverPayload: any;
  clientMandatoryFieldPayload: any;
  showSignUplink = false;

  subsciptionUuid = "";
  showResendLink = new BehaviorSubject<boolean>(true);

  companyUuid = '';
  tenantUuid = '';

  private lastPollingTime: number = 0;
  private readonly pollingInterval = PAYMENT_TIMELINE_POLL_INTERVAL_MS;
  private pollCount: number = 0;
  private readonly maxPollCount: number = PAYMENT_TIMELINE_MAX_POLL_COUNT;
  showRefreshButton = new BehaviorSubject<boolean>(false);
  private pollingSubscription?: Subscription;
  refreshButtonLoader = new BehaviorSubject<boolean>(false);
  progressBarMode= 'indeterminate';
  
  // Freeze flow properties
  isFreezeFlow = false;
  showOtherPaymentsModal$ = new BehaviorSubject<boolean>(false);
  totalAmountToBePaid = 0;

  private readonly destroy$ = new Subject<void>();

  get shouldShowChargePaymentFailure(): boolean {
    return (
      this.isChargeRetailFlow() &&
      (this.chargePaymentUnsuccessful() || this.showChargePaymentFailureState)
    );
  }

  isChargeRetailFlow(): boolean {
    return (
      this.linkType() === LinkType.Charge ||
      this.linkType() === LinkType.Product ||
      this.chargeOrderData$.value != null
    );
  }

  private markChargePaymentFailure(message?: string): void {
    if (!this.isChargeRetailFlow()) {
      return;
    }

    this.showChargePaymentFailureState = true;
    this.chargePaymentFailureMessage =
      message?.trim() || this.translate.instant('POS_DEVICE.FAILED_REASON');
    this.showLoader = false;
    this.progressBarMode = 'determinate';
    this.showRefreshButton.next(false);
    this.cd.markForCheck();
  }

  private handleChargePollingExhausted(): void {
    if (this.isChargeRetailFlow()) {
      this.markChargePaymentFailure(
        this.translate.instant('POS_DEVICE.PAYMENT_CONFIRMATION_TIMEOUT')
      );
      return;
    }

    this.showRefreshButton.next(true);
  }

  constructor() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  ngOnInit(): void {
    //this.getOrderDetails(this.uuid);
    this.componentInit();
    // Add visibility change listener
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  ngOnDestroy(): void {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  componentInit(): void {
    const companyInfoDetail$: Observable<any> = this.store.select(COMPANY_INFO);
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);

    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      if (this.tenantId > 0) {
        this.tenantUuid = tenant.selected_tenant.tenant_uuid;
        this.store.dispatch({ type: 'ROUTER_GO' });
        companyInfoDetail$.subscribe((responseData) => {
          this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
          this.translate.use(responseData?.selectedLang?.LANG_CODE);
          this.companyUuid = responseData?.compDetails?.uuid;
          this.getWaiverAndOrderDetails();
        });
      }
    });

   
    this.featureFlagService.getFlag$("staff_can_see_signup_link").pipe(takeUntil(this.destroy$)).subscribe((isEnabled: any) => {
      if(!isEnabled) {
        this.showSignUplink = true;
      }
    });


   
  }

  getWaiverAndOrderDetails(): void {
      forkJoin({
        waiverPayload: (!this.ispayment() && this.linkType() === LinkType.SignUp) ? this.subscriptionSignUp.GetWaivers(environment.BASE_URL, this.companyUuid, this.tenantUuid, this.uuid()).pipe(catchError((error) => {
          this.handleError(error);
          return of(null);
        })) : of(null),
        // , clientMandateInfo: (!this.ispayment) ? this.subscriptionSignUp.GetClientMandatoryField(environment.BASE_URL, this.uuid) : of(null)
         orderStatusQuotation: (!this.ispayment()) ? this.subscriptionSignUp.GetOrderDetails(environment.BASE_URL, this.companyUuid, this.tenantUuid, this.uuid()).pipe(catchError((error) => {
          this.handleError(error);
          return of(null);
        })) : of(null)
      }).subscribe({
       next: (result) => {
        const { waiverPayload, orderStatusQuotation } = result;
        //Order Object -------
        let istep = 0;

        this.showSkeleton$.next(false);

        // Check if this is a freeze order response
        if (orderStatusQuotation?.freezeOrderStatus) {
          const freezeOrder = orderStatusQuotation.freezeOrderStatus;
          this.freezeOrderData$.next(freezeOrder);
          this.isFreezeFlow = true;
          this.bitlyLink = freezeOrder.full_link;
          this.clientId = freezeOrder.frz_req_clt_id;
          this.subscriptionId = freezeOrder.frz_req_clt_subs_id;
          
          // Calculate total amount for freeze payment
          if (freezeOrder.freeze_type === 'PaidFreeze' && freezeOrder.frz_req_charge_amount_in_minor > 0) {
            this.totalAmountToBePaid = freezeOrder.frz_req_charge_amount_in_minor;
          }

          // Build timeline steps for freeze flow
          this.subscriptionSignUpStatusArray.push({ 
            key: SignUpStatus.Created, 
            title: "SENDPAYMENTLINK", 
            text: "SENDPAYMENTLINK_DTL", 
            icon: "fa fa-check", 
            step: (++istep), 
            status: "Done" 
          });

          if (freezeOrder.freeze_type === 'PaidFreeze') {
            this.subscriptionSignUpStatusArray.push({ 
              key: SignUpStatus.AwaitingPayment, 
              title: "AWAITINGPAYMENT", 
              text: "AWAITINGPAYMENT_DTL", 
              icon: "", 
              step: (++istep), 
              status: "Not Done" 
            });
          }

          this.subscriptionSignUpStatusArray.push({ 
            key: SignUpStatus.SignUpSuccessfull, 
            title: "FREEZECREATED", 
            text: "FREEZECREATED_DTL", 
            icon: "", 
            step: (++istep), 
            status: "Not Done" 
          });

          // Check freeze request status
          if (freezeOrder.frz_req_status === FreezeRequestStatus.FREEZE_APPLIED) {
            // Freeze has been applied - all steps successful
            this.isOffline = true;
            this.showLoader = false;
            this.setTimeLineStatus(SignUpStatus.SignUpSuccessfull);
          } else if (freezeOrder.frz_req_status === FreezeRequestStatus.APPROVED_PAYMENT_RECEIVED) {
            // Payment received, waiting for freeze to be applied
            this.isOffline = false;
            this.showLoader = true;
            this.setTimeLineStatus(SignUpStatus.AwaitingPayment);
          } else if (freezeOrder.frz_req_status === FreezeRequestStatus.REJECTED || 
                     freezeOrder.frz_req_status === FreezeRequestStatus.VOID || 
                     freezeOrder.frz_req_status === FreezeRequestStatus.EXPIRED) {
            // Freeze request failed
            this.isOffline = true;
            this.showLoader = false;
          }

          this.subscriptionSignUpStatus$.next(this.subscriptionSignUpStatusArray);
          this.startPolling();
        }
        else if (orderStatusQuotation?.signUpOrderSummaryResponse) {
          this.subsData$.next(orderStatusQuotation.signUpOrderSummaryResponse.order_details);
          
          // Check if this is a freeze flow
          const orderDetails = orderStatusQuotation.signUpOrderSummaryResponse.order_details;
          this.isFreezeFlow = orderDetails.link_type === LinkType.Freeze || orderDetails.link_type === 8;
          
          if (
            orderStatusQuotation.signUpOrderSummaryResponse.status == SignUpStatus.SignUpSuccessfull || orderStatusQuotation.signUpOrderSummaryResponse.status.toString() == SignUpStatus[SignUpStatus.SignUpSuccessfull]
            || orderStatusQuotation.signUpOrderSummaryResponse.status == SignUpStatus.SignUpFailed || orderStatusQuotation.signUpOrderSummaryResponse.status.toString() == SignUpStatus[SignUpStatus.SignUpFailed]
            || orderStatusQuotation.signUpOrderSummaryResponse.status == SignUpStatus.SignUpExpired || orderStatusQuotation.signUpOrderSummaryResponse.status.toString() == SignUpStatus[SignUpStatus.SignUpExpired]
          ) {
            this.isOffline = true;
            this.showLoader = false;
          }
          this.bitlyLink = orderStatusQuotation.signUpOrderSummaryResponse.full_link
          this.planIntervalType = orderStatusQuotation.signUpOrderSummaryResponse.order_details.billing_interval_type;
          
          // Calculate total amount for offline payment
          if (this.isFreezeFlow && orderDetails.charging_today) {
            this.totalAmountToBePaid = this.calculateTotalAmount(orderDetails);
          }

          //Order Object -------

          // Build timeline steps based on freeze flow or subscription flow
          if (this.isFreezeFlow) {
            // Freeze flow: Simplified steps
            this.subscriptionSignUpStatusArray.push({ key: SignUpStatus.Created, title: "SENDPAYMENTLINK", text: "SENDPAYMENTLINK_DTL", icon: "fa fa-check", step: (++istep), status: "Done", });

            if (!orderDetails.is_offline_payment)
              this.subscriptionSignUpStatusArray.push({ key: SignUpStatus.AwaitingPayment, title: "AWAITINGPAYMENT", text: "AWAITINGPAYMENT_DTL", icon: "", step: (++istep), status: "Not Done" });

            this.subscriptionSignUpStatusArray.push({ key: SignUpStatus.SignUpSuccessfull, title: "FREEZECREATED", text: "FREEZECREATED_DTL", icon: "", step: (++istep), status: "Not Done" });
          } else {
            // Subscription flow: Original steps
            this.subscriptionSignUpStatusArray.push({ key: SignUpStatus.Created, title: "SENDWAIVER", text: "SENDWAIVER_DTL", icon: "fa fa-check", step: (++istep), status: "Done", });

            if (waiverPayload && !this.ispayment && waiverPayload.items && waiverPayload.items.length > 0)
              this.subscriptionSignUpStatusArray.push({ key: SignUpStatus.WaiverDone, title: "CLIENTSIGN_WAIVER", text: "CLIENTSIGN_WAIVER_DTL", icon: "", step: (++istep), status: "Pending", });

            if (!this.ispayment())
              this.subscriptionSignUpStatusArray.push({ key: SignUpStatus.MandatoryInformationDone, title: "MEMBERDETAILS", text: "MEMBERDETAILS_DTL", icon: "", step: (++istep), status: "Not Done", });

            if (!orderDetails.is_offline_payment)
              this.subscriptionSignUpStatusArray.push({ key: SignUpStatus.AwaitingPayment, title: "AWAITINGPAYMENT", text: "AWAITINGPAYMENT_DTL", icon: "", step: (++istep), status: "Not Done" });

            this.subscriptionSignUpStatusArray.push({ key: SignUpStatus.SignUpSuccessfull, title: "SUBSCRIPTIONCREATED", text: "SUBSCRIPTIONCREATED_DTL", icon: "", step: (++istep), status: "Not Done" });
          }

         

          this.subscriptionSignUpStatus$.next(this.subscriptionSignUpStatusArray);

          if (this.skipPaymentPolling()) {
            const signupResponse = orderStatusQuotation.signUpOrderSummaryResponse;
            this.clientId = signupResponse.client_id;
            this.subscriptionId = signupResponse.client_subscription_id;
            this.subsciptionUuid = signupResponse.uuid;
            const timelineStatus = resolveSignupTimelineStatusAfterPayment(
              orderStatusQuotation,
              signupResponse.status
            );
            this.setTimeLineStatus(timelineStatus);
            const isSignupComplete = isSignupSuccessfulStatus(signupResponse.status);
            this.showLoader = !isSignupComplete;
            this.isOffline = isSignupComplete;
            this.showResendLink.next(true);
          } else {
            this.startPolling();
          }
        }
        else if (orderStatusQuotation?.chargeOrderStatus) {
          const chargeOrder = orderStatusQuotation.chargeOrderStatus;
          this.chargeOrderData$.next(chargeOrder);
          this.clientId = chargeOrder.client_id;
          this.bitlyLink = chargeOrder.full_link;

          const isTerminal = chargeOrder.status === 'Successfull' || chargeOrder.status === ChargeQuatationStatus.Successfull
            || chargeOrder.status === ChargeQuatationStatus[ChargeQuatationStatus.Successfull]
            || chargeOrder.status === 'Failed' || chargeOrder.status === ChargeQuatationStatus.Failed
            || chargeOrder.status === ChargeQuatationStatus[ChargeQuatationStatus.Failed]
            || chargeOrder.status === 'Expired' || chargeOrder.status === ChargeQuatationStatus.Expired
            || chargeOrder.status === ChargeQuatationStatus[ChargeQuatationStatus.Expired];

          if (isTerminal) {
            this.isOffline = true;
            this.showLoader = false;
          }

          // Build timeline steps for charge flow
          this.subscriptionSignUpStatusArray.push({
            key: SignUpStatus.Created,
            title: 'SENDPAYMENTLINK',
            text: 'SENDPAYMENTLINK_DTL',
            icon: 'fa fa-check',
            step: (++istep),
            status: 'Done'
          });

          if (!chargeOrder.billing_json?.is_offline) {
            this.subscriptionSignUpStatusArray.push({
              key: SignUpStatus.AwaitingPayment,
              title: 'AWAITINGPAYMENT',
              text: 'AWAITINGPAYMENT_DTL',
              icon: '',
              step: (++istep),
              status: 'Not Done'
            });
          }

          this.subscriptionSignUpStatusArray.push({
            key: SignUpStatus.SignUpSuccessfull,
            title: 'CHARGECREATED',
            text: 'CHARGECREATED_DTL',
            icon: '',
            step: (++istep),
            status: 'Not Done'
          });

          this.subscriptionSignUpStatus$.next(this.subscriptionSignUpStatusArray);

          if (this.chargePaymentUnsuccessful()) {
            this.markChargePaymentFailure();
          } else {
            this.startPolling();
          }
        }
       },
       error: (error) => {
        this.handleError(error);
       }
      });
  }

  getOrderDetails(uuid: string): void {

    this.subscriptionSignUp.GetOrderDetails(environment.BASE_URL, this.companyUuid, this.tenantUuid, uuid).subscribe({
      next: (getOrderStatusOfQuatation: GetOrderStatusOfQuotation) => {
        this.showSkeleton$.next(false);
        let subscriptionSignUpWithBillingJsonResponse = {} as SubscriptionSignUpWithBillingJsonResponse;

        if (getOrderStatusOfQuatation?.signUpOrderSummaryResponse) {

          subscriptionSignUpWithBillingJsonResponse = getOrderStatusOfQuatation.signUpOrderSummaryResponse;
          this.subsData$.next(subscriptionSignUpWithBillingJsonResponse.order_details);
          if (
            subscriptionSignUpWithBillingJsonResponse.status == SignUpStatus.SignUpSuccessfull || subscriptionSignUpWithBillingJsonResponse.status.toString() == SignUpStatus[SignUpStatus.SignUpSuccessfull]
            || subscriptionSignUpWithBillingJsonResponse.status == SignUpStatus.SignUpFailed || subscriptionSignUpWithBillingJsonResponse.status.toString() == SignUpStatus[SignUpStatus.SignUpFailed]
            || subscriptionSignUpWithBillingJsonResponse.status == SignUpStatus.SignUpExpired || subscriptionSignUpWithBillingJsonResponse.status.toString() == SignUpStatus[SignUpStatus.SignUpExpired]
          ) {
            this.isOffline = true;
            this.showLoader = false;
          }
          this.bitlyLink = subscriptionSignUpWithBillingJsonResponse.full_link
          this.planIntervalType = subscriptionSignUpWithBillingJsonResponse.order_details.billing_interval_type;
        }
        else if (getOrderStatusOfQuatation?.balanceOrderStatus) {
          // this has to be implemented   
          //balanceOrderStatus = getOrderStatusOfQuatation.balanceOrderStatus;
          //this.subsData$.next();
        }
        else if (getOrderStatusOfQuatation?.chargeOrderStatus) {
          const chargeOrderStatus = {} as GetChargeOrderStatus;
          // this has to be implemented
          //balanceOrderStatus = getOrderStatusOfQuatation.balanceOrderStatus;

          if (
            chargeOrderStatus.status == ChargeQuatationStatus.Successfull || chargeOrderStatus.status.toString() == ChargeQuatationStatus[ChargeQuatationStatus.Successfull]
            || chargeOrderStatus.status == ChargeQuatationStatus.Failed || chargeOrderStatus.status.toString() == ChargeQuatationStatus[ChargeQuatationStatus.Failed]
            || chargeOrderStatus.status == ChargeQuatationStatus.Expired || chargeOrderStatus.status.toString() == ChargeQuatationStatus[ChargeQuatationStatus.Expired]
          ) {
            this.isOffline = true;
            this.showLoader = false;
          }
        }
      }
    });

  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      // Stop polling when page is hidden
      this.stopPolling();
      // Store the last polling time
      this.lastPollingTime = Date.now();
    } else {
      // Page is visible again
      const timeSinceLastPoll = Date.now() - this.lastPollingTime;
      
      // If it's been longer than our polling interval since the last poll,
      // do an immediate poll and restart the interval
      if (timeSinceLastPoll >= this.pollingInterval) {
        this.GetOrderSubscriptionStatus(this.uuid());
      }
      
      // Restart polling if we haven't reached max count
      if (this.pollCount < this.maxPollCount) {
        this.startPolling();
      }
    }
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  startPolling(): void {
    this.orderSubscriptionStatusPollingService.stopPolling();

    // Prevent multiple polling subscriptions
    if (this.pollingSubscription || document.hidden) {
      return;
    }

    // Make an immediate call first if we haven't reached max count
    if (this.pollCount < this.maxPollCount) {
      this.GetOrderSubscriptionStatus(this.uuid());
    }

    // Start the interval
    this.pollingSubscription = interval(this.pollingInterval)
      .pipe(
        take(this.maxPollCount - this.pollCount) // Ensure we don't exceed maxPollCount
      )
      .subscribe({
        next: () => {
          if (document.hidden) {
            this.stopPolling();
            return;
          }
          
          this.pollCount++;
          this.GetOrderSubscriptionStatus(this.uuid());
          
          if (this.pollCount >= this.maxPollCount) {
            this.stopPolling();
            this.progressBarMode = 'determinate';
            this.handleChargePollingExhausted();
          }
        },
        complete: () => {
          this.stopPolling();
          this.handleChargePollingExhausted();
        }
      });
  }

  // Add method to manually refresh status
  refreshStatus(): void {
    this.progressBarMode = 'indeterminate';
    this.GetOrderSubscriptionStatus(this.uuid());
    setTimeout(() => {
      this.progressBarMode = 'determinate';
      this.cd.detectChanges();
    }, 1000);
  }

  GetOrderSubscriptionStatus(uuid: string): void {
    this.refreshButtonLoader.next(true);
    this.subscriptionSignUp.GetOrderSubscriptionStatus(environment.BASE_URL, this.companyUuid, this.tenantUuid, uuid).subscribe({
      next: (getOrderStatusOfQuatation: GetOrderStatusOfQuotation) => {
        this.refreshButtonLoader.next(false);
        
        // Handle freeze order status
        if (getOrderStatusOfQuatation?.freezeOrderStatus) {
          const freezeOrder = getOrderStatusOfQuatation.freezeOrderStatus;
          const onlineOutcome = evaluateOnlinePaymentPollOutcome(getOrderStatusOfQuatation);
          this.freezeOrderData$.next(freezeOrder);
          this.clientId = freezeOrder.frz_req_clt_id;
          this.subscriptionId = freezeOrder.frz_req_clt_subs_id;

          if (onlineOutcome === 'failed') {
            this.showLoader = false;
            this.stopPolling();
            const failureReason = getOnlinePaymentFailureReason(getOrderStatusOfQuatation);
            if (failureReason) {
              this.toastMessageService.showErrorMessage(failureReason);
            }
          } else if (freezeOrder.frz_req_status === FreezeRequestStatus.FREEZE_APPLIED) {
            // Freeze has been applied - mark all steps as successful
            this.setTimeLineStatus(SignUpStatus.SignUpSuccessfull);
            this.showLoader = false;
            this.isOffline = true;
            this.stopPolling();
            this.showResendLink.next(true);
          } else if (freezeOrder.frz_req_status === FreezeRequestStatus.APPROVED_PENDING_PAYMENT || freezeOrder.frz_req_status === FreezeRequestStatus.APPROVED_NO_PAYMENT_NEEDED) {
            this.setTimeLineStatus(SignUpStatus.AwaitingPayment);
            this.showResendLink.next(true);
          } else if (freezeOrder.frz_req_status === FreezeRequestStatus.APPROVED_PAYMENT_RECEIVED) {
            this.setTimeLineStatus(SignUpStatus.SignUpSuccessfull);
            this.showLoader = false;
            this.stopPolling();
            this.showResendLink.next(true);
          } else if (onlineOutcome === 'success') {
            this.setTimeLineStatus(SignUpStatus.AwaitingPayment);
            this.showResendLink.next(true);
          } else if (freezeOrder.frz_req_status === FreezeRequestStatus.REJECTED ||
                     freezeOrder.frz_req_status === FreezeRequestStatus.VOID || 
                     freezeOrder.frz_req_status === FreezeRequestStatus.EXPIRED) {
            // Freeze request failed/rejected/expired - stop polling
            this.showLoader = false;
            this.stopPolling();
          } else {
            // Still pending, continue polling
            this.setTimeLineStatus(SignUpStatus.AwaitingPayment);
          }
          this.subscriptionSignUpStatus$.next(this.subscriptionSignUpStatusArray);
        }
        // Handle subscription signup status
        else if (getOrderStatusOfQuatation?.signUpOrderSummaryResponse)   {
          const res = getOrderStatusOfQuatation.signUpOrderSummaryResponse;
          const onlineOutcome = evaluateOnlinePaymentPollOutcome(getOrderStatusOfQuatation);
          this.clientId = res.client_id;

          if (onlineOutcome === 'failed') {
            this.showLoader = false;
            this.stopPolling();
            const failureReason = getOnlinePaymentFailureReason(getOrderStatusOfQuatation);
            if (failureReason) {
              this.toastMessageService.showErrorMessage(failureReason);
            }
          } else if (isSignupSuccessfulStatus(res.status)) {
            this.setTimeLineStatus(SignUpStatus.SignUpSuccessfull);
            this.subscriptionId = res.client_subscription_id;
            this.subsciptionUuid = res.uuid;
            this.showResendLink.next(true);
            this.stopPolling();
          }
          else if (res.status == SignUpStatus.SignUpFailed || res.status.toString() == SignUpStatus[SignUpStatus.SignUpFailed]
            || res.status == SignUpStatus.SignUpExpired || res.status.toString() == SignUpStatus[SignUpStatus.SignUpExpired]) {
            this.showLoader = false;
            this.stopPolling();
          }
          else if (onlineOutcome === 'success') {
            this.setTimeLineStatus(SignUpStatus.AwaitingPayment);
            this.showResendLink.next(true);
          }
          else {
            this.setTimeLineStatus(res.status);
          }
          this.subscriptionSignUpStatus$.next(this.subscriptionSignUpStatusArray);
        }
        // Handle charge order status
        else if (getOrderStatusOfQuatation?.chargeOrderStatus) {
          const chargeOrder = getOrderStatusOfQuatation.chargeOrderStatus;
          const onlineOutcome = evaluateOnlinePaymentPollOutcome(getOrderStatusOfQuatation);
          this.chargeOrderData$.next(chargeOrder);
          this.clientId = chargeOrder.client_id;

          const statusStr = chargeOrder.status?.toString() ?? '';
          if (onlineOutcome === 'failed') {
            this.markChargePaymentFailure(
              getOnlinePaymentFailureReason(getOrderStatusOfQuatation)
            );
            this.stopPolling();
          } else if (
            statusStr === 'Successfull' || statusStr === ChargeQuatationStatus[ChargeQuatationStatus.Successfull]
            || chargeOrder.status === ChargeQuatationStatus.Successfull
          ) {
            this.setTimeLineStatus(SignUpStatus.SignUpSuccessfull);
            this.showLoader = false;
            this.stopPolling();
            this.showResendLink.next(true);
          } else if (
            statusStr === 'Failed' || statusStr === ChargeQuatationStatus[ChargeQuatationStatus.Failed]
            || chargeOrder.status === ChargeQuatationStatus.Failed
            || statusStr === 'Expired' || statusStr === ChargeQuatationStatus[ChargeQuatationStatus.Expired]
            || chargeOrder.status === ChargeQuatationStatus.Expired
          ) {
            this.markChargePaymentFailure();
            this.stopPolling();
          } else if (onlineOutcome === 'success') {
            this.setTimeLineStatus(SignUpStatus.AwaitingPayment);
            this.showResendLink.next(true);
          } else if (
            statusStr === 'AwaitingPayment' || statusStr === ChargeQuatationStatus[ChargeQuatationStatus.AwaitingPayment]
            || chargeOrder.status === ChargeQuatationStatus.AwaitingPayment
          ) {
            this.setTimeLineStatus(SignUpStatus.AwaitingPayment);
          }
          this.subscriptionSignUpStatus$.next(this.subscriptionSignUpStatusArray);
        }
      },
      error: (err) => {
        console.error('Status check error:', err);
        // Don't stop polling on error unless we've reached max count
        if (this.pollCount >= this.maxPollCount) {
          this.stopPolling();
          this.handleChargePollingExhausted();
        }
      }
    });
  }

  // firePaymentHideEvent(event: any) {
  //   this.showPaymentStatusSidebar = false;
  //   this.hidePaymentSummaryPopup.emit(this.showPaymentStatusSidebar);
  // }

  payingToday(oData: SubscriptionSignUpOrderSummaryResponse): number {
    return (
      this.nonRecurringAmount(oData) +
      this.nonRecurringTaxAmount(oData) +
      this.proRataAmount(oData) +
      this.proRataTaxAmount(oData) +
      this.chargeAmount(oData) +
      this.chargeTaxAmount(oData)
    );
  }

  proRataAmount(oData: SubscriptionSignUpOrderSummaryResponse): number {
    let amount = 0;
    if (
      oData.charging_today?.service_first_charge?.services
    ) {
      for (
        let i = 0;
        i < oData.charging_today.service_first_charge.services.length;
        i++
      ) {
        amount =
          amount +
          oData.charging_today.service_first_charge.services[i]
            .taxable_cost_in_minor_curr;
      }
    }
    return amount;
  }

  proRataTaxAmount(oData: SubscriptionSignUpOrderSummaryResponse): number {
    let amount = 0;
    if (
      oData.charging_today?.service_first_charge?.services
    ) {
      for (
        let i = 0;
        i < oData.charging_today.service_first_charge.services.length;
        i++
      ) {
        for (
          let j = 0;
          j <
          oData.charging_today.service_first_charge.services[i].tax
            .tax_line_items.length;
          j++
        ) {
          amount =
            amount +
            oData.charging_today.service_first_charge.services[i].tax
              .tax_line_items[j].amt_in_minor_curr;
        }
      }
    }

    return amount;
  }

  recurringAmount(oData: SubscriptionSignUpOrderSummaryResponse): number {
    let amount = 0;
    if (oData.charging_recurring?.services) {
      for (let i = 0; i < oData.charging_recurring.services.length; i++) {
        amount =
          amount +
          oData.charging_recurring.services[i].taxable_cost_in_minor_curr;
      }
    }

    return amount;
  }

  recurringTaxAmount(oData: SubscriptionSignUpOrderSummaryResponse): number {
    let amount = 0;
    if (oData.charging_recurring?.services) {
      for (let i = 0; i < oData.charging_recurring.services.length; i++) {
        for (
          let j = 0;
          j < oData?.charging_recurring?.services[i]?.tax?.tax_line_items?.length;
          j++
        ) {
          amount =
            amount +
            oData?.charging_recurring?.services[i]?.tax?.tax_line_items[j]
              .amt_in_minor_curr;
        }
      }
    }

    return amount;
  }

  nonRecurringAmount(oData: SubscriptionSignUpOrderSummaryResponse): number {
    let amount = 0;
    if (
      oData.charging_today?.service_non_recurring?.services
    ) {
      for (
        let i = 0;
        i < oData.charging_today.service_non_recurring.services.length;
        i++
      ) {
        amount =
          amount +
          oData.charging_today.service_non_recurring.services[i]
            .taxable_cost_in_minor_curr;
      }
    }
    return amount;
  }

  nonRecurringTaxAmount(oData: SubscriptionSignUpOrderSummaryResponse): number {
    let amount = 0;
    if (
      oData.charging_today?.service_non_recurring?.services
    ) {
      for (let i = 0;i < oData.charging_today.service_non_recurring.services.length;i++) {
        for (
          let j = 0;
          j <
          oData.charging_today.service_non_recurring.services[i].tax
            .tax_line_items.length;
          j++
        ) {
          amount =
            amount +
            oData.charging_today.service_non_recurring.services[i].tax
              .tax_line_items[j].amt_in_minor_curr;
        }
      }
    }

    return amount;
  }

  chargeAmount(oData: SubscriptionSignUpOrderSummaryResponse): number {
    let amount = 0;
    if (oData.charging_today?.signup_charges) {
      for (let i = 0; i < oData.charging_today.signup_charges.length; i++) {
        amount =
          amount +
          oData.charging_today.signup_charges[i].chrgmst_net_cost_in_minor_curr;
      }
    }
    return amount;
  }

  chargeTaxAmount(oData: SubscriptionSignUpOrderSummaryResponse): number {
    let amount = 0;
    if (oData.charging_today?.signup_charges) {
      for (let i = 0; i < oData.charging_today.signup_charges.length; i++) {
        for (
          let j = 0;
          j < oData.charging_today.signup_charges[i].tax.tax_line_items.length;
          j++
        ) {
          amount =
            amount +
            oData.charging_today.signup_charges[i].tax.tax_line_items[j]
              .amt_in_minor_curr;
        }
      }
    }
    return amount;
  }

  allServiceNameHtml(oData: SubscriptionSignUpOrderSummaryResponse): string {
    const serviceCategoryName: string[] = [];
    const serviceCategory: any[] = [];
    oData.charging_recurring?.services?.forEach(
      (oService: SubscriptionSignupService) => {
        if (
          serviceCategoryName.indexOf(oService.category_name_pri_lang) == -1
        ) {
          serviceCategoryName.push(oService.category_name_pri_lang);
          serviceCategory.push({
            name: oService.category_name_pri_lang,
            isBaseMembership: oService.is_base_membership,
          });
        }
      }
    );

    oData.charging_today?.service_non_recurring?.services?.forEach(
      (oService: SubscriptionSignupService) => {
        if (
          serviceCategoryName.indexOf(oService.category_name_pri_lang) == -1
        ) {
          serviceCategoryName.push(oService.category_name_pri_lang);
          serviceCategory.push({
            name: oService.category_name_pri_lang,
            isBaseMembership: oService.is_base_membership,
          });
        }
      }
    );

    const str = `
        <div>
          <div class="mt-2 mb-0 md:mb-1 text-xs text-600">
              {{First}}
              {{Others}}
          </div>
        </div>
    `;
    // <div class="text-right ml-3">{{recurringAmount}}</div>

    let other = '';
    let first= '';
    for (let i = 0; i < serviceCategory.length; i++) {
      if (i == 0)
        first =
          serviceCategory[i].name +
          (serviceCategory[i].isBaseMembership
            ? ' <span class="inline-block px-2 line-height-4 text-xxs surface-200 border-round"> Base Membership </span>'
            : '');
      else
        other =
          other +
          '<br class="md:hidden"> + ' +
          serviceCategory[i].name +
          (serviceCategory[i].isBaseMembership
            ? ' <span class="inline-block px-2 line-height-4 text-xxs surface-200 border-round" > Base Membership </span>'
            : '');
    }

    const strAmount = this.currencyPipe.transform(
      this.recurringAmount(oData) + this.recurringTaxAmount(oData),
      undefined
    );
    return str
      .replace('{{First}}', first)
      .replace('{{Others}}', other)
      .replace('{{recurringAmount}}', strAmount);
  }

  serviceNonRecurringNameHtml(
    oData: SubscriptionSignUpOrderSummaryResponse
  ): string {
    const serviceCategoryName: string[] = [];
    const serviceCategory: any[] = [];
    oData.charging_today?.service_non_recurring?.services?.forEach(
      (oService: SubscriptionSignupService) => {
        if (
          serviceCategoryName.indexOf(oService.category_name_pri_lang) == -1
        ) {
          serviceCategoryName.push(oService.category_name_pri_lang);
          serviceCategory.push({
            name: oService.category_name_pri_lang,
            isBaseMembership: oService.is_base_membership,
          });
        }
      }
    );

    const str = `
      <div>
        <div>One time add-on's</div>
        <div class="mt-2 mb-0 md:mb-1 text-xs text-60">
          {{First}}
          {{Others}}
        </div>
      </div>
      <div class="text-right ml-3">{{recurringAmount}}</div>
    `;

    let other = '';
    let first = '';
    for (let i = 0; i < serviceCategory.length; i++) {
      if (i == 0) first = serviceCategory[i].name;
      else
        other = other + '<br class="md:hidden"> + ' + serviceCategory[i].name;
    }

    const strAmount = this.currencyPipe.transform(
      this.nonRecurringAmount(oData),
      undefined
    );
    return str
      .replace('{{First}}', first)
      .replace('{{Others}}', other)
      .replace('{{recurringAmount}}', strAmount);
  }

  getTaxDetails(oData: SubscriptionSignUpOrderSummaryResponse): string {
   const tax: string[] = [];
    oData.charging_today?.signup_charges?.forEach(
      (oCharges: SubscriptionSignupCharges) => {
        if (
          oCharges.tax?.tax_line_items &&
          oCharges.tax.tax_line_items.length > 0
        )
          oCharges.tax.tax_line_items.forEach(
            (oTax: SubscriptionSignupTaxLineItem) => {
              if (tax.indexOf(oTax.name_pri_lang) == -1)
                tax.push(oTax.name_pri_lang);
            }
          );
      }
    );

    oData.charging_today?.service_first_charge?.services?.forEach(
      (oService: SubscriptionSignupService) => {
        if (
          oService.tax?.tax_line_items &&
          oService.tax.tax_line_items.length > 0
        )
          oService.tax.tax_line_items.forEach(
            (oTax: SubscriptionSignupTaxLineItem) => {
              if (tax.indexOf(oTax.name_pri_lang) == -1)
                tax.push(oTax.name_pri_lang);
            }
          );
      }
    );

    oData.charging_today?.service_non_recurring?.services?.forEach(
      (oService: SubscriptionSignupService) => {
        if (
          oService.tax?.tax_line_items &&
          oService.tax.tax_line_items.length > 0
        )
          oService.tax.tax_line_items.forEach(
            (oTax: SubscriptionSignupTaxLineItem) => {
              if (tax.indexOf(oTax.name_pri_lang) == -1)
                tax.push(oTax.name_pri_lang);
            }
          );
      }
    );

    return tax.join('</br>');
  }

  ordinalNumber(n: number | null | undefined): string {
    if (n) {
      const s = ['th', 'st', 'nd', 'rd'];
      const v: number = n % 100;
      return n.toString() + (s[(v - 20) % 10] || s[v] || s[0]).toString();
    } else return '';
  }

  get isWeeklyPlan(): boolean {
    return (
      (this.planIntervalType &&
        this.planIntervalType?.toString() ==
        PlanIntervalType[PlanIntervalType.Week]) ||
      false
    );
  }

  get isDayPlan(): boolean {
    return false;
    // return (this.planIntervalType && this.planIntervalType?.toString() == PlanIntervalType[PlanIntervalType.Day]) || false;
  }

  get isMonthlyPlan(): boolean {
    return (
      (this.planIntervalType &&
        this.planIntervalType?.toString() ==
        PlanIntervalType[PlanIntervalType.Month]) ||
      false
    );
  }

  ResendPaymentLink(mode: string) {
    const channels: any[] = [];
    channels.push(mode);
    this.subscriptionService.resendPaymentLink(this.tenantId, this.clientId, this.uuid(), channels).subscribe({
      next: () => {
        this.translate.get('SUBSCRIPTIONS.MSG_RESEND_LINK')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
      },
      error: (error) => {
        if (error.originalError.status == 200) {
          this.toastMessageService.showSuccess(error.originalError.error.text);
        }
        else if(error.originalError.error &&  Array.isArray(error.originalError.error) && error.originalError.error.length>0)
          {
            error.originalError.error.forEach((element: NotificationError) => {
              if(element.success)
                {
                  this.translate.get('SEND_EMAIL.MSG.EMAIL_SEND')
                  .subscribe((translation) => {
                    this.toastMessageService.showSuccess(translation);
                  });
                }
                else
                this.toastMessageService.showErrorMessage(element.error.errorMessage);
            });
          }
        else {
          this.handleError(error);
        }
      },
    });
  }

  handleError(error: any) {
    this.toastMessageService.showError(error);
  }

  setTimeLineStatus(res: SignUpStatus) {

    const index = this.subscriptionSignUpStatusArray.findIndex((o) => o.key == res
      || o.key.toString().toLowerCase() == SignUpStatus[res].toString().toLowerCase());
    for (let i = 0; i <= index; i++) {
      this.subscriptionSignUpStatusArray[i].status = "Done";
      this.subscriptionSignUpStatusArray[i].icon = "fa fa-check";
    }

    if (index != this.subscriptionSignUpStatusArray.length - 1) {
      this.subscriptionSignUpStatusArray[index + 1].status = "Pending";
    }

    if (res == SignUpStatus.SignUpFailed || res.toString() == SignUpStatus[SignUpStatus.SignUpFailed]
      || res == SignUpStatus.SignUpExpired || res.toString() == SignUpStatus[SignUpStatus.SignUpExpired]) {

      this.subscriptionSignUpStatusArray[this.subscriptionSignUpStatusArray.length - 1].status = "Error";
      this.subscriptionSignUpStatusArray[this.subscriptionSignUpStatusArray.length - 1].icon = "Not Done";

    }
    // this.showLoader = false;
    // this.timerSubscription.unsubscribe();

  }

  setHidePaymentSideBar() {
    this.ngOnDestroy();
    this.hidePaymentSummaryPopup.emit(false);
  }

  GoToCLientProfile(tab:string) {
    this.ngOnDestroy();
    //this.hidePaymentSummaryPopup.emit(false);
    if (this.isFreezeFlow && this.clientId) {
      // For freeze flow, emit event to open client sidebar with transactions tab
      this.GoToClientProfile.emit({
        clientId: this.clientId, 
        tab: tab || 'transactions',
        subscriptionId: this.subscriptionId,
        freezeRequestId: this.freezeOrderData$.value?.frz_req_id,
        openSidebar: true // Flag to indicate sidebar should be opened
      });
    } else {
      if(tab) {
        this.GoToClientSubscription.emit({tab: tab});
      } else {
        const tab= this.freezeOrderData$.value ? 'transactions' : this.chargeOrderData$.value ? 'transactions' : 'subscriptions';
        this.GoToClientSubscription.emit({tab: tab});
      }
    }
  }
  
  // Calculate total amount for offline payment
  calculateTotalAmount(orderDetails: SubscriptionSignUpOrderSummaryResponse): number {
    return (
      this.nonRecurringAmount(orderDetails) +
      this.nonRecurringTaxAmount(orderDetails) +
      this.proRataAmount(orderDetails) +
      this.proRataTaxAmount(orderDetails) +
      this.chargeAmount(orderDetails) +
      this.chargeTaxAmount(orderDetails)
    );
  }

  // Calculate total charge order amount from billing_json
  calculateChargeOrderTotal(chargeOrder: GetChargeOrderStatus): number {
    let total = 0;
    chargeOrder.billing_json?.order_charges?.forEach(charge => {
      total += charge.chrgmst_net_cost_in_minor_curr;
      charge.tax?.tax_line_items?.forEach(taxItem => {
        total += taxItem.amt_in_minor_curr;
      });
    });
    return total;
  }

  getTaxTotalForCharge(charge: ChargeOrderItem): number {
    return charge.tax?.tax_line_items?.reduce((sum, item) => sum + item.amt_in_minor_curr, 0) ?? 0;
  }
  
  // Open offline payment dialog
  openOfflinePayment(): void {
    this.showOtherPaymentsModal$.next(true);
  }
  
  // Handle offline payment dialog close
  hideOtherPaymentDialog(evt: any): void {
    if (evt?.paymentSignUp) {
      // Handle freeze payment submission
      // This would need to call the freeze payment API
      // For now, just close the dialog
      this.showOtherPaymentsModal$.next(false);
      this.cd.detectChanges();
    } else {
      this.showOtherPaymentsModal$.next(false);
      this.cd.detectChanges();
    }
  }
  
  // Close offline payment dialog
  closeOtherPaymentDialog(event: any): void {
    this.showOtherPaymentsModal$.next(false);
    this.cd.detectChanges();
  }

  isRecurring(oData: SubscriptionSignUpOrderSummaryResponse): boolean {
    return (
      oData?.subscription_category &&
      (oData.subscription_category == PlanCategory.Contract ||
        oData.subscription_category.toString() ==
        PlanCategory[PlanCategory.Contract] ||
        oData.subscription_category == PlanCategory.PayAsYouGo ||
        oData.subscription_category.toString() ==
        PlanCategory[PlanCategory.PayAsYouGo])
    );
  }

  // Get freeze request status display text
  getFreezeStatusText(status: number): string {
    const statusMap: { [key: number]: string } = {
      [FreezeRequestStatus.PENDING_REVIEW]: 'Pending Review',
      [FreezeRequestStatus.REQUIRE_MORE_INFO]: 'Requires More Info',
      [FreezeRequestStatus.APPROVED_PENDING_PAYMENT]: 'Approved - Pending Payment',
      [FreezeRequestStatus.APPROVED_PAYMENT_RECEIVED]: 'Approved - Payment Received',
      [FreezeRequestStatus.APPROVED_NO_PAYMENT_NEEDED]: 'Approved - No Payment Needed',
      [FreezeRequestStatus.FREEZE_APPLIED]: 'Freeze Applied',
      [FreezeRequestStatus.REJECTED]: 'Rejected',
      [FreezeRequestStatus.VOID]: 'Void',
      [FreezeRequestStatus.EXPIRED]: 'Expired'
    };
    return statusMap[status] || 'Unknown';
  }

  // Get freeze action type display text
  getFreezeActionTypeText(actionType: string): string {
    const actionTypeMap: { [key: string]: string } = {
      'FullFreeze': 'Full Freeze',
      'FreezeReschedule': 'Reschedule Freeze',
      'FreezeStop': 'Stop Freeze',
      'FreezeCancel': 'Cancel Freeze'
    };
    return actionTypeMap[actionType] || actionType;
  }

}

